/**
 * AdminPhotoboothMediaPage ✦ iter222f — /admin/photobooth-media
 *
 * Single-screen control surface for the /photobooth-hire cover
 * photos. Three sections (Booths · Packages · Add-ons), each row
 * shows the current image (admin override or hardcoded default
 * or empty), an "Upload photo" button (replaces any override),
 * and a "Clear" button (drops the override back to default).
 *
 * Reuses the same auth shape as AdminPublicGalleriesPage —
 * /login?next=/admin/photobooth-media gates entry.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const SECTION_LABELS = {
  booths: 'The Booth Collection',
  packages: 'Packages',
  addons: 'Add-ons',
};

// Backend uses singular kind keys; frontend groups under plural section keys.
const KIND_FOR_SECTION = { booths: 'booth', packages: 'package', addons: 'addon' };

const AdminPhotoboothMediaPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [catalogue, setCatalogue] = useState({ booths: [], packages: [], addons: [] });
  const [loading, setLoading] = useState(true);

  // Auth gate — mirrors AdminPublicGalleriesPage shape.
  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/photobooth-media');
  }, [auth?.user, navigate]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/photobooth-media`);
      setCatalogue({
        booths: r.data.booths || [],
        packages: r.data.packages || [],
        addons: r.data.addons || [],
      });
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error('Could not load photobooth media catalogue.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0F0B1A', color: IVORY,
        fontFamily: SERIF, padding: '80px 24px', textAlign: 'center',
      }}>
        <p style={{ fontStyle: 'italic', color: IVORY_DIM }}>Loading the catalogue ✦</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-photobooth-media-page" style={{
      minHeight: '100vh', background: '#0F0B1A', color: IVORY,
      fontFamily: SERIF, padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', gap: 16,
          marginBottom: 34, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 8,
            }}>✦ Photobooth media</div>
            <h1 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(28px, 4vw, 38px)', margin: 0,
              letterSpacing: '-0.02em',
            }}>Cover photos for /photobooth-hire</h1>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              color: IVORY_DIM, fontSize: 14,
              margin: '10px 0 0', maxWidth: 600, lineHeight: 1.6,
            }}>
              Upload a single cover photo per booth, package or add-on. Clearing a
              photo returns the card to its branded placeholder.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            data-testid="admin-back-link"
            style={{
              background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
              color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13, padding: '8px 18px', borderRadius: 999,
              cursor: 'pointer', letterSpacing: '0.02em',
            }}
          >← Admin home</button>
        </div>

        {/* Sections */}
        {(['booths', 'packages', 'addons']).map((sectionKey) => (
          <MediaSection
            key={sectionKey}
            sectionKey={sectionKey}
            kind={KIND_FOR_SECTION[sectionKey]}
            items={catalogue[sectionKey]}
            onChanged={load}
          />
        ))}
      </div>
    </div>
  );
};

const MediaSection = ({ sectionKey, kind, items, onChanged }) => {
  if (!items || items.length === 0) return null;
  return (
    <section
      data-testid={`media-section-${sectionKey}`}
      style={{
        marginBottom: 36, padding: '24px 0',
        borderTop: `1px solid ${GOLD_FAINT}`,
      }}
    >
      <h2 style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: 19,
        margin: '0 0 18px', color: IVORY, letterSpacing: '-0.005em',
      }}>{SECTION_LABELS[sectionKey]}</h2>
      <div style={{
        display: 'grid', gap: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      }}>
        {items.map((it) => (
          <MediaRow
            key={`${kind}-${it.slug}`}
            kind={kind}
            item={it}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
};

const MediaRow = ({ kind, item, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useRef(null);

  const onPickFile = () => fileInputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Max 25MB per photo.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/admin/photobooth-media/${kind}/${item.slug}/photo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Photo uploaded ✦');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      // Reset file input so picking the same file twice still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onClear = async () => {
    if (item.image_source !== 'admin') return;
    if (!window.confirm(`Clear the photo for ${item.name}?`)) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/admin/photobooth-media/${kind}/${item.slug}/photo`);
      toast.success('Photo cleared ✦');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed.');
    } finally {
      setClearing(false);
    }
  };

  const preview = mediaUrl(item.image_url);

  return (
    <div
      data-testid={`media-row-${kind}-${item.slug}`}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid rgba(255,255,255,0.08)`,
        borderRadius: 10, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Preview */}
      <div style={{
        position: 'relative', aspectRatio: '4 / 3',
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {preview ? (
          <img
            data-testid={`media-preview-${kind}-${item.slug}`}
            src={preview}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{
            fontFamily: SERIF, fontStyle: 'italic',
            color: 'rgba(255,255,255,0.42)', fontSize: 13,
            letterSpacing: '0.04em',
          }}>No photo yet</span>
        )}
        {item.image_source === 'admin' && (
          <span style={{
            position: 'absolute', top: 10, left: 10,
            background: 'rgba(212,170,82,0.22)',
            color: GOLD, border: `1px solid ${GOLD_FAINT}`,
            borderRadius: 999, padding: '3px 10px',
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5,
            letterSpacing: '0.16em', textTransform: 'uppercase',
          }}>Custom ✦</span>
        )}
      </div>

      {/* Meta + actions */}
      <div style={{ padding: '14px 16px 16px' }}>
        <h3 style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 15,
          margin: '0 0 2px', color: IVORY,
        }}>{item.name}</h3>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11.5, color: IVORY_DIM,
          margin: '0 0 12px', letterSpacing: '0.02em',
        }}>{item.slug}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploading}
            data-testid={`upload-${kind}-${item.slug}`}
            style={{
              padding: '7px 14px', borderRadius: 999,
              background: GOLD, color: '#0F0B1A', border: 'none',
              fontFamily: SERIF, fontWeight: 600, fontSize: 12.5,
              cursor: uploading ? 'wait' : 'pointer',
              opacity: uploading ? 0.55 : 1,
            }}
          >{uploading ? 'Uploading…' : (item.image_source === 'admin' ? 'Replace photo' : '+ Upload photo')}</button>
          {item.image_source === 'admin' && (
            <button
              type="button"
              onClick={onClear}
              disabled={clearing}
              data-testid={`clear-${kind}-${item.slug}`}
              style={{
                padding: '7px 14px', borderRadius: 999,
                background: 'transparent', color: IVORY_DIM,
                border: `1px solid rgba(255,255,255,0.12)`,
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
                cursor: clearing ? 'wait' : 'pointer',
                opacity: clearing ? 0.55 : 1,
              }}
            >{clearing ? 'Clearing…' : 'Clear'}</button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          data-testid={`file-input-${kind}-${item.slug}`}
          onChange={onFile}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default AdminPhotoboothMediaPage;
